/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ----------------------------------------------------------------------------
 *   OffendersPanel (Spec 256 — Backup Cost Governance, US4 / T045)
 *   "Which backups actually drive egress?" — a horizontal bar of the top
 *   egress/transfer offenders (by share of attributed movement bytes) + an
 *   expandable table with each offender's route class and attribution basis.
 *   A labeled non-backup residual is shown separately (never folded into a
 *   plan). EmptyState until the cost collectors have measured egress. Directional
 *   — bytes + share, never currency.
 * ============================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, Badge, DataTable, Select, DashboardPanel, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { fmtBytes, COLOR } from '../dashboard/CommandCenterCharts';
import { costApi, type Offender, type OffenderGrouping } from '../../api/cost';

const routeColor = (routeClass: string): string => {
  if (routeClass.includes('nat-internet')) return COLOR.failed;
  if (routeClass.includes('cross-region')) return COLOR.atRisk;
  if (routeClass === 'unknown') return COLOR.cancelled;
  return COLOR.succeeded;
};

const WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

export const OffendersPanel: React.FC = () => {
  const [windowDays, setWindowDays] = React.useState(30);
  const [by, setBy] = React.useState<OffenderGrouping>('plan');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['backup-cost-offenders', windowDays, by],
    queryFn: () => costApi.offenders(windowDays, by),
  });

  const controls = (
    <div className="flex items-center gap-2">
      <Select
        aria-label="Group offenders by"
        value={by}
        onChange={(e) => setBy(e.target.value as OffenderGrouping)}
        options={[
          { value: 'plan', label: 'By plan' },
          { value: 'entity', label: 'By entity' },
        ]}
      />
      <Select
        aria-label="Offenders window"
        value={String(windowDays)}
        onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
        options={WINDOWS}
      />
    </div>
  );

  const columns: DataTableColumn<Offender>[] = [
    { key: 'name', header: by === 'plan' ? 'Plan' : 'Entity', render: (r) => <span className="font-medium text-[var(--color-text-primary)]">{r.name}</span>, sortValue: (r) => r.name },
    { key: 'bytes', header: 'Moved', render: (r) => <span className="tabular-nums text-[var(--color-text-secondary)]">{fmtBytes(r.bytes)}</span>, sortValue: (r) => r.bytes },
    { key: 'share', header: 'Share', render: (r) => <span className="tabular-nums text-[var(--color-text-secondary)]">{r.sharePct}%</span>, sortValue: (r) => r.sharePct },
    { key: 'route', header: 'Route', render: (r) => <Badge variant={r.routeClass.includes('nat-internet') ? 'danger' : r.routeClass.includes('cross-region') ? 'warning' : r.routeClass === 'unknown' ? 'default' : 'success'}>{r.routeClass}</Badge>, sortValue: (r) => r.routeClass },
    { key: 'basis', header: 'Basis', render: (r) => <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-tertiary)]">{r.basis}</span>, sortValue: (r) => r.basis },
  ];

  return (
    <DashboardPanel
      title="Egress offenders"
      subtitle="Which backups move the most data over the expensive paths (NAT egress + cross-region transfer). Ranked by share of attributed movement bytes — directional, never a dollar figure."
      titleInfo="Populates once the cost collectors have measured egress. Non-backup traffic is shown as a separate, labeled residual — never attributed to a plan."
      actions={controls}
    >
      {isLoading ? (
        <LoadingState rows={5} />
      ) : isError || !data ? (
        <ErrorState title="Could not load offenders" message="Please try again." onRetry={() => refetch()} />
      ) : data.offenders.length === 0 ? (
        <EmptyState
          title="No measured egress yet"
          message="No backup egress or cross-region transfer has been attributed for this window. This fills in after the NAT-egress and cost collectors run."
        />
      ) : (
        <div className="space-y-4">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.offenders.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip
                  formatter={(_value, _name, entry) => {
                    const o = (entry as unknown as { payload?: Offender }).payload;
                    return [o ? `${o.sharePct}% · ${fmtBytes(o.bytes)}` : '', o?.routeClass ?? ''];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="sharePct" radius={[0, 4, 4, 0]}>
                  {data.offenders.slice(0, 10).map((o) => (
                    <Cell key={o.key} fill={routeColor(o.routeClass)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {data.residual && (
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3 text-sm text-[var(--color-text-secondary)]">
              {data.residual.label}: <span className="tabular-nums">{fmtBytes(data.residual.bytes)}</span> of movement not attributable to a backup plan.
            </div>
          )}

          <Card padding="none">
            <DataTable columns={columns} rows={data.offenders} rowKey={(r) => r.key} minWidth="44rem" />
          </Card>
        </div>
      )}
    </DashboardPanel>
  );
};

export default OffendersPanel;
